/**
 * LeetCode API service for fetching discussion posts and details
 * 
 * SOLVES THE PRODUCTION URL ISSUE:
 * 
 * PROBLEM: In production (AWS Amplify), the app was calling:
 *   https://main.d356sai2mwwryj.amplifyapp.com/api/leetcode/graphql
 * 
 * This happened because:
 * 1. setupProxy.js only works in development with React dev server
 * 2. In production, there's no proxy middleware running
 * 3. The /api/leetcode path becomes part of the Amplify domain
 * 4. CORS errors - The wrong domain causes cross-origin request failures
 * 
 * SOLUTION:
 * 1. Environment-aware configuration (development vs production)
 * 2. Development: Uses local proxy (/api/leetcode) via setupProxy.js
 * 3. Production: Uses reliable CORS proxy services
 * 4. Multiple fallback options if one proxy fails
 * 5. Proper error handling and logging
 * 
 * CONFIGURATION:
 * - Development: /api/leetcode/graphql → setupProxy.js → https://leetcode.com/graphql
 * - Production: https://api.allorigins.win/raw?url=https://leetcode.com/graphql
 */

import { getLeetCodeUrl, shouldUseProxy, getFallbackProxy } from '../config/api';

// Get LeetCode GraphQL URL
const LEETCODE_GRAPHQL_URL = getLeetCodeUrl('graphql');

// Multiple CORS proxy options for production (in order of reliability)
const PRODUCTION_PROXIES = [
  'https://api.allorigins.win/raw?url=https://leetcode.com',
  'https://corsproxy.io/?https://leetcode.com',
  'https://thingproxy.freeboard.io/fetch/https://leetcode.com',
  'https://cors-anywhere.herokuapp.com/https://leetcode.com'
];


// Helper function to try multiple proxies
const tryProxies = async (query, variables, operationName) => {
  for (let i = 0; i < PRODUCTION_PROXIES.length; i++) {
    const proxy = PRODUCTION_PROXIES[i];
    try {
      const response = await fetch(`${proxy}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
          operationName
        })
      });

      if (response.ok) {
        return response;
      }
    } catch (error) {
      // Try next proxy
    }
  }
  
  throw new Error('All production proxies failed');
};

// Fetch discussion posts by company name
export const fetchDiscussionPosts = async (companyName) => {
  try {
    const query = `
      query discussPostItems($orderBy: ArticleOrderByEnum, $keywords: [String]!, $tagSlugs: [String!], $skip: Int, $first: Int) {
        ugcArticleDiscussionArticles(
          orderBy: $orderBy
          keywords: $keywords
          tagSlugs: $tagSlugs
          skip: $skip
          first: $first
        ) {
          totalNum
          pageInfo {
            hasNextPage
          }
          edges {
            node {
              uuid
              title
              slug
              summary
              author {
                realName
                userAvatar
                userSlug
                userName
                nameColor
                certificationLevel
                activeBadge {
                  icon
                  displayName
                }
              }
              isOwner
              isAnonymous
              isSerialized
              scoreInfo {
                scoreCoefficient
              }
              articleType
              thumbnail
              summary
              createdAt
              updatedAt
              status
              isLeetcode
              canSee
              canEdit
              isMyFavorite
              myReactionType
              topicId
              hitCount
              reactions {
                count
                reactionType
              }
              tags {
                name
                slug
                tagType
              }
              topic {
                id
                topLevelCommentCount
              }
            }
          }
        }
      }
    `;

    const variables = {
      orderBy: "HOT",
      keywords: [companyName],
      tagSlugs: [],
      skip: 0,
      first: 50
    };

    let response;
    
    if (shouldUseProxy()) {
      // Development: Use local proxy
      response = await fetch(LEETCODE_GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
          operationName: "discussPostItems"
        })
      });
    } else {
      // Production: Try direct call first, then fallback to proxies
      try {
        response = await fetch('https://leetcode.com/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Referer': 'https://leetcode.com',
            'Origin': 'https://leetcode.com',
            'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36'
          },
          body: JSON.stringify({
            query,
            variables,
            operationName: "discussPostItems"
          })
        });
        
        if (!response.ok) {
          throw new Error(`Direct call failed with status: ${response.status}`);
        }
      } catch (error) {
        response = await tryProxies(query, variables, "discussPostItems");
      }
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data?.ugcArticleDiscussionArticles?.edges || [];
  } catch (error) {
    console.error('Error fetching discussion posts:', error);
    throw error;
  }
};

// Fetch discussion detail by topic ID
export const fetchDiscussionDetail = async (topicId) => {
  try {
    const query = `
      query discussPostDetail($topicId: ID!) {
        ugcArticleDiscussionArticle(topicId: $topicId) {
          uuid
          title
          slug
          summary
          content
          isSlate
          author {
            realName
            userAvatar
            userSlug
            userName
            nameColor
            certificationLevel
            activeBadge {
              icon
              displayName
            }
          }
          isOwner
          isAnonymous
          isSerialized
          isAuthorArticleReviewer
          scoreInfo {
            scoreCoefficient
          }
          articleType
          thumbnail
          summary
          createdAt
          updatedAt
          status
          isLeetcode
          canSee
          canEdit
          isMyFavorite
          myReactionType
          topicId
          hitCount
          reactions {
            count
            reactionType
          }
          tags {
            name
            slug
            tagType
          }
          topic {
            id
            topLevelCommentCount
          }
        }
      }
    `;

    const variables = {
      topicId: topicId.toString()
    };

    let response;
    
    if (shouldUseProxy()) {
      // Development: Use local proxy
      response = await fetch(LEETCODE_GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
          operationName: "discussPostDetail"
        })
      });
    } else {
      // Production: Try direct call first, then fallback to proxies
      try {
        response = await fetch('https://leetcode.com/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Referer': 'https://leetcode.com',
            'Origin': 'https://leetcode.com',
            'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36'
          },
          body: JSON.stringify({
            query,
            variables,
            operationName: "discussPostDetail"
          })
        });
        
        if (!response.ok) {
          throw new Error(`Direct call failed with status: ${response.status}`);
        }
      } catch (error) {
        response = await tryProxies(query, variables, "discussPostDetail");
      }
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data?.ugcArticleDiscussionArticle;
  } catch (error) {
    console.error('Error fetching discussion detail:', error);
    throw error;
  }
};

// Transform LeetCode data to match our discussion format
export const transformDiscussionData = (leetCodeData) => {
  if (!leetCodeData) return null;

  return {
    id: leetCodeData.topicId || leetCodeData.uuid,
    title: leetCodeData.title || '',
    description: leetCodeData.summary || '',
    content: leetCodeData.content || '',
    author: leetCodeData.author?.userName || leetCodeData.author?.realName || 'Anonymous',
    authorAvatar: leetCodeData.author?.userAvatar || leetCodeData.author?.userName?.charAt(0) || 'A',
    company: 'LeetCode', // Default company name
    tags: leetCodeData.tags?.map(tag => tag.name) || [],
    createdAt: leetCodeData.createdAt || new Date().toISOString(),
    updatedAt: leetCodeData.updatedAt || leetCodeData.createdAt || new Date().toISOString(),
    comments: leetCodeData.topic?.topLevelCommentCount || 0,
    hitCount: leetCodeData.hitCount || 0,
    reactions: leetCodeData.reactions || [],
    topicId: leetCodeData.topicId,
    uuid: leetCodeData.uuid
  };
};
